import { fn } from 'storybook/test';
import { action } from 'storybook/actions';
import { useState } from 'react';

import type { Meta, StoryFn } from '@storybook/react';
import type { CheckboxLeaf, CheckboxBranch, CheckboxTreeProps } from './CheckboxTree';

import { CheckboxTree } from './CheckboxTree';

import { Chip } from 'components/Chip';

const createPeriodTree = (periodIndices: string[], numberOfPeriods: number, periodPerSemester: number) => {
  const allPeriods = Array.from({ length: numberOfPeriods }, (_, idx) => idx + 1);

  const periodValueToLabel = (value: string) => {
    const semester = Math.ceil(parseInt(value) / periodPerSemester);
    const period = semester === periodPerSemester ? '' : `.${parseInt(value) % periodPerSemester || periodPerSemester}`;
    return `${semester}${period}`;
  };

  const periodTree = allPeriods.map((periodIndex) => ({
    itemName: periodValueToLabel(String(periodIndex)),
    itemValue: periodIndex,
    disabled: false,
  }));

  const periodTreeObject = allPeriods.reduce((periods, currentPeriod, periodIndex) => {
    const semester = Math.ceil(currentPeriod / periodPerSemester);
    if (periodIndices.includes(String(periodIndex + 1))) {
      if (!periods[semester]) {
        periods[semester] = {
          itemName: `Semester ${semester}`,
          children: [],
        };
      }
      periods[semester].children.push({
        ...periodTree[periodIndex],
        itemValue: String(periodTree[periodIndex].itemValue),
      } as CheckboxLeaf);
    }
    return periods;
  }, [] as CheckboxBranch[]);

  return periodTreeObject;
};

const NUMBER_OF_PERIODS = 7;
const PERIOD_PER_SEMESTER = 3;

const MOCK_CHECKBOX_TREE = createPeriodTree(
  ['1', '2', '3', '4', '5', '6', '7'],
  NUMBER_OF_PERIODS,
  PERIOD_PER_SEMESTER
);

const codeString = '<CheckboxTree  title={} checkboxTree={} selectedValues={} onChange={} />';

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Organisms/CheckboxTree',
  component: CheckboxTree,
  parameters: {
    inspectComponent: CheckboxTree,
    codeString: codeString,
  },
} as Meta<CheckboxTreeProps>;

const Template: StoryFn<CheckboxTreeProps> = (args) => <CheckboxTree {...args} />;

export const CheckboxTreeExample = Template.bind({});
CheckboxTreeExample.args = {
  title: 'Periode',
  checkboxTree: MOCK_CHECKBOX_TREE,
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  selectedValues: ['3', '4'],
  onChange: fn(),
};

const PERIOD_SEGMENT = 3;

const periodValueToLabel = (value: string) => {
  const semester = Math.ceil(parseInt(value) / PERIOD_SEGMENT);
  const period = semester === PERIOD_SEGMENT ? '' : `.${parseInt(value) % PERIOD_SEGMENT || PERIOD_SEGMENT}`;
  return `${semester}${period}`;
};

const periodLabelToValue = (value: string): string => {
  const [row, col] = value.split('.').map(Number);
  return `${(row - 1) * PERIOD_SEGMENT + (col || 1)}`;
};

export const CheckboxTreeWithChips: StoryFn<CheckboxTreeProps> = () => {
  const [activeFilters, setActiveFilters] = useState<string[]>(['5', '4', '1']);

  const handleRemoveActiveFilter = (filterToRemove: string) => {
    const newActiveFilters = JSON.parse(JSON.stringify(activeFilters)).filter(
      (filter: string) => filter !== periodLabelToValue(filterToRemove)
    );

    setActiveFilters(() => newActiveFilters);
    action('onChange value:')(newActiveFilters);
  };

  const handlePeriodChange = (value: string[]) => {
    setActiveFilters([...value]);
    action('onChange value:')(value);
  };

  return (
    <div style={{ width: '15rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', width: '100%', marginBottom: '1rem' }}>
        {activeFilters &&
          activeFilters.map((activeFilter, index) => (
            <Chip
              label={periodValueToLabel(activeFilter)}
              onDelete={handleRemoveActiveFilter}
              key={`${index}_${activeFilter}`}
            />
          ))}
      </div>
      <CheckboxTree
        title="Periode"
        checkboxTree={MOCK_CHECKBOX_TREE}
        selectedValues={activeFilters}
        onChange={handlePeriodChange}
      />
    </div>
  );
};
